"use client"

import { useMemo, useState } from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"

export type LabeledOption = {
  label: string
  value: string
}

interface LabeledMultiSelectProps {
  options: LabeledOption[]
  selectedValues: string[]
  onSelectedValuesChange: (next: string[]) => void

  label?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string

  /** NOTE: keeping your requested prop name (typo and all) */
  bagdeTitle?: string
}

export function LabeledMultiSelect({
  options,
  selectedValues,
  onSelectedValuesChange,
  label = "Options",
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  emptyText = "No options found.",
  bagdeTitle = "SELECTED",
}: LabeledMultiSelectProps) {
  const [open, setOpen] = useState(false)

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>()
    for (const opt of options) map.set(opt.value, opt.label)
    return map
  }, [options])

  const handleSelect = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectedValuesChange(selectedValues.filter((v) => v !== value))
      return
    }
    onSelectedValuesChange([...selectedValues, value])
  }

  const handleRemove = (value: string) => {
    onSelectedValuesChange(selectedValues.filter((v) => v !== value))
  }

  const clearAll = () => onSelectedValuesChange([])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-teal-600">{label}</Label>
        {selectedValues.length > 0 && (
          <Button variant="link" size="sm" className="text-teal-600 p-0 h-auto" onClick={clearAll}>
            Clear all
          </Button>
        )}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between text-left font-normal bg-transparent"
          >
            {selectedValues.length > 0 ? `${selectedValues.length} selected` : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((opt, idx) => (
                  <CommandItem
                    key={`${opt.value}-${idx}`}
                    value={`${opt.label} ${opt.value}`}
                    onSelect={() => handleSelect(opt.value)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn("mr-2 h-4 w-4", selectedValues.includes(opt.value) ? "opacity-100" : "opacity-0")}
                    />
                    {opt.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedValues.map((value) => (
            <Badge key={value} variant="secondary" className="flex items-center gap-1">
              {bagdeTitle ? <span className="text-xs bg-teal-700 text-white px-1 rounded">{bagdeTitle}</span> : null}
              {labelByValue.get(value) ?? value}
              <X className="h-3 w-3 cursor-pointer" onClick={() => handleRemove(value)} />
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
